import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react";
import AddEditMockups from "../../../pages/orders/AddEditMockups";
import MockupGeneratorModal from "../../swagstore/mockups/MockupGeneratorModal";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import type { OrderMockup } from "../../../../../../models/Order/OrderMockup";
import type { MockupComment } from "../../../../../../models/Order/MockupComment";
import type { Swagpack } from "../../../../../../models/Swagstore/Packs";
import CatalogProductsAPI from "../../../../api/CatalogProductsAPI";
import {
  activeCatalogSkus,
  catalogSkuForRequest,
  PRODUCT_LEVEL_SKU,
  psdMatchesCatalogSku,
} from "../../swagstore/mockups/catalogSkuChoices";

export type OrderMockupModalTab = "view" | "generate" | "upload";

/** Fixed height for all tabs — sized for full generate canvas (600×450) + chrome. */
const UNIFIED_MODAL_HEIGHT = "min(836px, 92vh)";

interface OrderUnifiedMockupModalProps {
  open: boolean;
  onClose: () => void;
  activeTab: OrderMockupModalTab;
  onTabChange: (tab: OrderMockupModalTab, mockupId?: number) => void;
  mockupId?: number | null;
  productId: number;
  orderId?: number;
  productName?: string;
  mockups: OrderMockup[];
  comments: MockupComment[];
  canDeleteMockup?: boolean;
  isCommentsFetching?: boolean;
  isMainMockupImageLoading?: boolean;
  archived?: number;
  updateMockupComment?: (mockupComment: MockupComment) => void;
  fetchMockupComment?: (mockupId: number) => void;
  deleteMockup?: (mockupId: number) => void;
  selectedMockupId?: number;
  onSelectPsdId: (id: number | null) => void;
  onGenerateMockup: (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    template_required?: boolean;
    printing_type?: string;
    logo_size?: string;
    rgb_code?: string;
    cmyk_code?: string;
    variant_sku: string;
  }) => void | Promise<void>;
  onUploadLogoToLibrary: (file: File) => void;
  onDeleteUploadedLogo: (logoId: number) => Promise<void>;
  onManualUploadMockup?: (payload: {
    id: number;
    file: File;
    variant_sku: string;
  }) => Promise<void>;
  onUploadMockupFiles?: (files: File[], variantSku: string) => Promise<void>;
}

function EditMethodCard({
  title,
  description,
  icon,
  accent,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  onClick: () => void;
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        flex: 1,
        p: 2.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "all 0.2s ease",
        background: `linear-gradient(135deg, ${accent}08 0%, #ffffff 100%)`,
        "&:hover": {
          borderColor: accent,
          boxShadow: `0 8px 24px ${accent}22`,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${accent}14`,
          color: accent,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <CustomTypography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
        {title}
      </CustomTypography>
      <CustomTypography variant="caption" color="text.secondary">
        {description}
      </CustomTypography>
    </Paper>
  );
}

const OrderUnifiedMockupModal: React.FC<OrderUnifiedMockupModalProps> = observer(
  ({
    open,
    onClose,
    activeTab,
    onTabChange,
    mockupId,
    productId,
    orderId,
    productName,
    mockups,
    comments,
    canDeleteMockup,
    isCommentsFetching,
    isMainMockupImageLoading,
    archived = 0,
    updateMockupComment,
    fetchMockupComment,
    deleteMockup,
    selectedMockupId,
    onSelectPsdId,
    onGenerateMockup,
    onUploadLogoToLibrary,
    onDeleteUploadedLogo,
    onManualUploadMockup,
    onUploadMockupFiles,
  }) => {
    const { mockupConfigStore, projectSourcingOpsStore } = useStores();
    const { showError } = useNotification();
    const [editChoiceMockupId, setEditChoiceMockupId] = useState<number | null>(null);
    const [isDialogFullScreen, setIsDialogFullScreen] = useState(false);
    const [catalogSkus, setCatalogSkus] = useState<string[]>([]);
    const [selectedVariantSku, setSelectedVariantSku] = useState(PRODUCT_LEVEL_SKU);
    const appliedEditSkuFor = useRef<number | null>(null);

    useEffect(() => {
      if (!open) {
        setEditChoiceMockupId(null);
        setIsDialogFullScreen(false);
        setSelectedVariantSku(PRODUCT_LEVEL_SKU);
        appliedEditSkuFor.current = null;
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;
      if (!mockupId) {
        if (appliedEditSkuFor.current != null) {
          appliedEditSkuFor.current = null;
          setSelectedVariantSku(PRODUCT_LEVEL_SKU);
        }
        return;
      }
      if (appliedEditSkuFor.current === mockupId) return;
      const mockup = mockups.find((item) => item.id === mockupId);
      if (!mockup) return;
      appliedEditSkuFor.current = mockupId;
      const sku = String(mockup.variant_sku ?? "").trim();
      setSelectedVariantSku(sku || PRODUCT_LEVEL_SKU);
    }, [open, mockupId, mockups]);

    useEffect(() => {
      if (!open || !productId) return;
      let cancelled = false;
      new CatalogProductsAPI()
        .fetchVariants(productId)
        .then((rows) => {
          if (cancelled) return;
          setCatalogSkus(activeCatalogSkus(rows));
        })
        .catch(() => {
          if (!cancelled) setCatalogSkus([]);
        });
      return () => {
        cancelled = true;
      };
    }, [open, productId]);

    const skuChoices = useMemo(() => {
      const editingSku = String(
        mockups.find((item) => item.id === mockupId)?.variant_sku ?? "",
      ).trim();
      if (
        !editingSku ||
        catalogSkus.some((sku) => sku.toUpperCase() === editingSku.toUpperCase())
      ) {
        return catalogSkus;
      }
      return [editingSku, ...catalogSkus];
    }, [catalogSkus, mockupId, mockups]);

    const product = useMemo<Swagpack | null>(
      () =>
        productId
          ? ({
            id: productId,
            label: productName || `Product ${productId}`,
            products: [
              {
                product_id: productId,
                name: productName || `Product ${productId}`,
                variants: skuChoices.map((sku) => ({ variant_sku: sku })),
              },
            ],
          } as Swagpack)
          : null,
      [skuChoices, productId, productName],
    );

    const isEditMode = Boolean(mockupId);

    const psdTemplatesForSku = useMemo(
      () =>
        mockupConfigStore.psdTemplates.filter((template) =>
          psdMatchesCatalogSku(template.variant_sku, selectedVariantSku),
        ),
      [mockupConfigStore.psdTemplates, selectedVariantSku],
    );

    useEffect(() => {
      if (!open) return;
      const stillValid = psdTemplatesForSku.some(
        (template) => template.id === mockupConfigStore.selectedPsdId,
      );
      if (stillValid) return;
      const nextId = psdTemplatesForSku[0]?.id ?? null;
      if (mockupConfigStore.selectedPsdId !== nextId) {
        onSelectPsdId(nextId);
      }
    }, [
      mockupConfigStore.selectedPsdId,
      onSelectPsdId,
      open,
      psdTemplatesForSku,
    ]);

    const requireSku = () => {
      const sku = catalogSkuForRequest(selectedVariantSku);
      if (!sku) {
        showError(
          "SKU required",
          "Select a SKU. New mockups are saved against one SKU.",
        );
        return null;
      }
      return sku;
    };

    const isBusy =
      projectSourcingOpsStore.generatingOrderMockup ||
      Boolean(
        isEditMode &&
        mockupId &&
        projectSourcingOpsStore.updatingOrderMockupId === mockupId,
      );

    const handleTabSwitch = (tab: OrderMockupModalTab) => {
      if (tab === "view") {
        onTabChange("view");
        return;
      }
      if (isEditMode && (tab === "generate" || tab === "upload")) {
        onTabChange(tab, mockupId ?? undefined);
        return;
      }
      onTabChange(tab);
    };

    const handleEditMockupRequest = (id: number) => {
      setEditChoiceMockupId(id);
    };

    const handleEditMethodSelect = (tab: "generate" | "upload") => {
      if (!editChoiceMockupId) return;
      onTabChange(tab, editChoiceMockupId);
      setEditChoiceMockupId(null);
    };

    return (
      <>
        <Dialog
          open={open}
          onClose={() => {
            if (isBusy) return;
            onClose();
          }}
          maxWidth="lg"
          fullWidth
          fullScreen={isDialogFullScreen}
          PaperProps={{
            sx: {
              position: "relative",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 24px 48px rgba(15, 23, 42, 0.16)",
              ...(isDialogFullScreen
                ? {
                  m: 0,
                  width: "100%",
                  maxWidth: "100%",
                  height: "100%",
                  minHeight: "100%",
                  maxHeight: "100%",
                  borderRadius: 0,
                }
                : {
                  width: "100%",
                  maxWidth: 1120,
                  height: UNIFIED_MODAL_HEIGHT,
                  minHeight: UNIFIED_MODAL_HEIGHT,
                  maxHeight: UNIFIED_MODAL_HEIGHT,
                  borderRadius: 3,
                }),
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  "linear-gradient(90deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)",
                zIndex: 1,
              },
            }}
          >
            <DialogTitle
              sx={{
                pr: 12,
                pt: 2.5,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <CustomTypography variant="heading5" fontWeight={700}>
                {productName || "Product Mockups"}
              </CustomTypography>
              <CustomTypography variant="caption" color="text.secondary">
                View, generate, or upload mockups in one place
              </CustomTypography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ position: "absolute", right: 12, top: 12 }}
              >
                <Tooltip title={isDialogFullScreen ? "Exit full page" : "Full page"} arrow>
                  <IconButton
                    aria-label={isDialogFullScreen ? "Exit full page" : "Full page"}
                    onClick={() => setIsDialogFullScreen((prev) => !prev)}
                    disabled={isBusy}
                    sx={{
                      bgcolor: "grey.50",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    {isDialogFullScreen ? (
                      <CloseFullscreenOutlinedIcon fontSize="small" />
                    ) : (
                      <OpenInFullOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <IconButton
                  aria-label="Close"
                  onClick={onClose}
                  disabled={isBusy}
                  sx={{
                    bgcolor: "grey.50",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </DialogTitle>

            <Box
              sx={{
                px: 3,
                pb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, value) => handleTabSwitch(value as OrderMockupModalTab)}
                sx={{
                  minHeight: 44,
                  flex: 1,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    minHeight: 44,
                    color: "text.secondary",
                  },
                  "& .Mui-selected": { color: "primary.main" },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab value="view" label="View mockups" />
                <Tab value="generate" label="Generate" />
                <Tab value="upload" label="Upload" />
              </Tabs>
              {activeTab !== "view" && skuChoices.length > 0 && (
                <Box sx={{ width: 220, flexShrink: 0, mb: 0.5 }}>
                  <AppDropDown
                    title="SKU"
                    isMultiSelect={false}
                    list={[
                      { id: PRODUCT_LEVEL_SKU, value: "All SKUs" },
                      ...skuChoices.map((sku) => ({ id: sku, value: sku })),
                    ]}
                    value={selectedVariantSku}
                    disabled={
                      isEditMode && catalogSkuForRequest(selectedVariantSku) != null
                    }
                    onSingleSelectionChange={(id) => {
                      if (isEditMode && catalogSkuForRequest(selectedVariantSku) != null) {
                        return;
                      }
                      setSelectedVariantSku(id);
                    }}
                    placeholderText="All SKUs"
                    width="220px"
                    height="32px"
                    fontSize="0.75rem"
                  />
                </Box>
              )}
            </Box>
          </Box>

          {isEditMode && activeTab !== "view" && (
            <Box
              sx={{
                px: 3,
                py: 1.25,
                bgcolor: "rgba(79, 70, 229, 0.06)",
                borderBottom: "1px solid",
                borderColor: "rgba(79, 70, 229, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                aria-label="Back to mockups"
                onClick={() => onTabChange("view")}
                sx={{ bgcolor: "background.paper" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <CustomTypography variant="body2" fontWeight={600}>
                Editing mockup #{mockupId}
              </CustomTypography>
              <CustomTypography variant="caption" color="text.secondary">
                — {activeTab === "generate" ? "regenerate with logo" : "replace image"}
              </CustomTypography>
            </Box>
          )}

          <DialogContent
            dividers
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              p: 2,
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              bgcolor: "grey.50",
            }}
          >
            {isBusy && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: (theme) => theme.zIndex.modal + 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "all",
                }}
              >
                <CircularProgress size={48} />
              </Box>
            )}
            {activeTab === "view" && (
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <AddEditMockups
                  embedded
                  archived={archived}
                  canDeleteMockup={canDeleteMockup}
                  product={{ id: productId, name: productName || "" }}
                  mockups={mockups}
                  comments={comments}
                  updateMockupComment={updateMockupComment}
                  fetchMockupComment={fetchMockupComment}
                  isCommentsFetching={isCommentsFetching}
                  deleteMockup={deleteMockup}
                  isMainMockupImageLoading={isMainMockupImageLoading}
                  selectedMockupId={selectedMockupId}
                  onNavigateToTab={(tab, mockupId) => onTabChange(tab, mockupId)}
                  onEditMockup={handleEditMockupRequest}
                />
              </Paper>
            )}

            {(activeTab === "generate" || activeTab === "upload") && (
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  py: 1,
                  px: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <MockupGeneratorModal
                  embedded
                  open
                  hideInternalTabs
                  controlledTab={activeTab}
                  product={product}
                  onClose={onClose}
                  initialTab={activeTab}
                  placementTypes={mockupConfigStore.placementTypes}
                  psdTemplates={psdTemplatesForSku}
                  variantSku={selectedVariantSku}
                  onVariantSkuChange={setSelectedVariantSku}
                  selectedPsdId={mockupConfigStore.selectedPsdId}
                  loadingPsd={mockupConfigStore.loadingPsd}
                  savedMockups={[]}
                  mockupOutputs={null}
                  mockupReferenceId={null}
                  generatingMockup={projectSourcingOpsStore.generatingOrderMockup}
                  updatingMockupId={projectSourcingOpsStore.updatingOrderMockupId}
                  deletingGeneratedMockupId={null}
                  editMode={isEditMode}
                  editMockupId={mockupId ?? null}
                  hideGeneratedMockupsTab
                  onSelectPsdId={onSelectPsdId}
                  onGenerateMockup={(payload) => {
                    const sku = requireSku();
                    if (!sku) return;
                    return onGenerateMockup({ ...payload, variant_sku: sku });
                  }}
                  uploadedLogos={projectSourcingOpsStore.mockupGeneratorLogos}
                  loadingUploadedLogos={projectSourcingOpsStore.loadingMockupGeneratorLogos}
                  onUploadLogoToLibrary={onUploadLogoToLibrary}
                  onDeleteUploadedLogo={onDeleteUploadedLogo}
                  onDeleteMockup={async () => { }}
                  onManualUploadMockup={
                    onManualUploadMockup
                      ? async (payload) => {
                          const sku = requireSku();
                          if (!sku) return;
                          await onManualUploadMockup({
                            ...payload,
                            variant_sku: sku,
                          });
                        }
                      : undefined
                  }
                  onUploadMockupFiles={async (files) => {
                    const sku = requireSku();
                    if (!sku || !onUploadMockupFiles) return;
                    await onUploadMockupFiles(files, sku);
                  }}
                  onError={(title, message) => showError(title, message || "")}
                />
              </Paper>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={editChoiceMockupId != null}
          onClose={() => setEditChoiceMockupId(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 0.5,
              overflow: "visible",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, pt: 2.5, pr: 6, position: "relative" }}>
            <CustomTypography variant="heading6" fontWeight={700}>
              How do you want to update this mockup?
            </CustomTypography>
            <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Choose generate to place a logo on a template, or upload to replace with your own image.
            </CustomTypography>
            <IconButton
              aria-label="Close"
              onClick={() => setEditChoiceMockupId(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                bgcolor: "grey.50",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1, pb: 3, overflow: "visible" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ py: 0.5 }}
            >
              <EditMethodCard
                title="Generate"
                description="Place a logo on a PSD template and create a new mockup."
                icon={<AutoAwesomeOutlinedIcon />}
                accent="#4F46E5"
                onClick={() => handleEditMethodSelect("generate")}
              />
              <EditMethodCard
                title="Upload"
                description="Replace this mockup with an image from your device."
                icon={<CloudUploadOutlinedIcon />}
                accent="#7C3AED"
                onClick={() => handleEditMethodSelect("upload")}
              />
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

export default OrderUnifiedMockupModal;
